/**
 * ApiError
 *
 * Neden: Axios'un ham hatası (AxiosError) consumer tarafında zor kullanılır —
 * bizim domain'imize ait `ApiError` sınıfı ile HTTP status, kod, mesaj ve
 * alan-bazlı hatalar tek tip halinde yüzey kazanır. Component tarafı sadece
 * `err.status === 422 && err.fieldErrors` kontrolü yapar.
 */
import { AxiosError } from 'axios'
import { type ApiErrorBody } from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: unknown
  readonly fieldErrors?: Record<string, string>

  constructor(
    status: number,
    code: string,
    message: string,
    options?: { details?: unknown; fieldErrors?: Record<string, string> }
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = options?.details
    this.fieldErrors = options?.fieldErrors
  }

  /** Tür korumalı unwrap — catch bloklarında `if (isApiError(e))` ile kullanılır. */
  static is(err: unknown): err is ApiError {
    return err instanceof ApiError
  }
}

/**
 * Axios hatasını ApiError'a çevirir.
 * Neden: Interceptor'da ve dış çağrılarda aynı dönüşüm — kod tekrarını önler.
 */
export function fromAxiosError(err: AxiosError): ApiError {
  const status = err.response?.status ?? 0
  const body = err.response?.data as ApiErrorBody | undefined
  const payload = body?.error

  if (payload) {
    return new ApiError(status, payload.code, payload.message, {
      details: payload.details,
      fieldErrors: payload.fieldErrors,
    })
  }

  // Backend sözleşmesine uymayan ham hata (ağ, proxy, timeout...).
  return new ApiError(
    status,
    status === 0 ? 'NETWORK_ERROR' : 'UNKNOWN_ERROR',
    err.message || 'Bilinmeyen bir hata oluştu.'
  )
}

/** `isApiError` alias — daha rahat okumak için. */
export const isApiError = ApiError.is
